/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Label,
    makeStyles,
    Radio,
    RadioGroup,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Add24Regular, Delete24Regular, Edit24Regular, Eye24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { BerichtsTerminItem } from '../../models/BerichtsTerminItem';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    toolbar: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        alignItems: 'center',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
        flex: 1,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    errorMessage: {
        color: tokens.colorStatusDangerForeground1,
        marginBottom: tokens.spacingVerticalL,
    },
    sectionHeader: {
        fontWeight: 700,
        fontSize: tokens.fontSizeBase400,
        marginTop: tokens.spacingVerticalL,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorBrandForeground1,
    },
    sectionDescription: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
        marginBottom: tokens.spacingVerticalM,
    },
    readOnlyField: {
        padding: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        fontWeight: 500,
    },
    colActions: {
        width: '100px',
        minWidth: '100px',
    },
    colNumber: {
        width: '120px',
        minWidth: '120px',
    },
    colDate: {
        width: '120px',
        minWidth: '120px',
    },
    dragHandle: {
        cursor: 'grab',
        color: tokens.colorNeutralForeground3,
        userSelect: 'none',
        '&:active': {
            cursor: 'grabbing',
        },
    },
    draggingRow: {
        opacity: 0.5,
        backgroundColor: tokens.colorNeutralBackground2,
    },
});

interface BerichtsterminForm extends Omit<BerichtsTerminItem, 'datumVon' | 'datumBis' | 'datumFaelligkeit'> {
    datumVon: Date | null;
    datumBis: Date | null;
    datumFaelligkeit: Date | null;
}

interface ProjektmanagementBerichteProps {
    project: ProjektItem | null;
}

const ProjektmanagementBerichte: React.FC<ProjektmanagementBerichteProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [berichte, setBerichte] = useState<BerichtsTerminItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isViewOnly, setIsViewOnly] = useState(false);

    // Delete confirmation dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<BerichtsTerminItem | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Drag-and-drop states
    const [draggedItem, setDraggedItem] = useState<any | null>(null);
    const [dragOverItem, setDragOverItem] = useState<any | null>(null);
    const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);

    const [formData, setFormData] = useState<BerichtsterminForm>({
        itemId: 0,
        projektID: project?.projektID || 0,
        bezeichnung: '',
        datumVon: null,
        datumBis: null,
        datumFaelligkeit: null,
        bemerkungen: '',
        isZwischenevaluierung: false,
        isInhaltlicherBericht: false,
        isFinanziellerBericht: false,
        isEingereicht: false,
        isAngenommen: false,
        orderBy: 0,
    } as any);

    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch reports
    const fetchBerichte = useCallback(async () => {
        if (!project?.projektID || !dataService) return;

        try {
            setIsLoading(true);
            setError(null);
            const data = await dataService.getBerichtsterminListe(project.projektID);
            console.log('Fetched reports:', data);
            setBerichte(data);
        } catch (err) {
            console.error('Error loading reports:', err);
            setError('Error loading reports');
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService]);

    useEffect(() => {
        fetchBerichte();
    }, [fetchBerichte]);

    // Get "insert after" options
    const insertAfterOptions = useMemo(() => {
        const options = [];

        // Add "Insert at Beginning" option
        options.push({
            label: 'Insert at Beginning',
            value: '-2',
        });

        // Add existing reports
        berichte.forEach(item => {
            options.push({
                label: item.prefixTitle + ': ' + item.bezeichnung + ' (' + (item.datumVon ? `${new Date(item.datumVon).toLocaleDateString()}` : '') + (item.datumBis ? ` - ${new Date(item.datumBis).toLocaleDateString()}` : '') + ')',
                value: item.itemId.toString(),
            });
        });

        return options;
    }, [berichte]);

    const handleAddNew = () => {
        setEditingId(null);
        setIsViewOnly(false);
        setFormData({
            itemId: 0,
            projektID: project?.projektID || 0,
            bezeichnung: '',
            datumVon: null,
            datumBis: null,
            datumFaelligkeit: null,
            bemerkungen: '',
            isZwischenevaluierung: false,
            isInhaltlicherBericht: false,
            isFinanziellerBericht: false,
            isEingereicht: false,
            isAngenommen: false,
            orderBy: -2, // Default to beginning
        } as any);
        setIsDrawerOpen(true);
    };

    const handleView = (item: BerichtsTerminItem) => {
        setEditingId(item.itemId);
        setIsViewOnly(true);
        setFormData({
            ...item,
            datumVon: item.datumVon ? new Date(item.datumVon) : null,
            datumBis: item.datumBis ? new Date(item.datumBis) : null,
            datumFaelligkeit: item.datumFaelligkeit ? new Date(item.datumFaelligkeit) : null,
        } as any);
        setIsDrawerOpen(true);
    };

    const handleEdit = (item: BerichtsTerminItem) => {
        setEditingId(item.itemId);
        setIsViewOnly(false);
        setFormData({
            ...item,
            datumVon: item.datumVon ? new Date(item.datumVon) : null,
            datumBis: item.datumBis ? new Date(item.datumBis) : null,
            datumFaelligkeit: item.datumFaelligkeit ? new Date(item.datumFaelligkeit) : null,
        } as any);
        setIsDrawerOpen(true);
    };

    const handleDelete = async (itemId: number) => {
        const item = berichte.find(b => b.itemId === itemId);
        if (item) {
            setItemToDelete(item);
            setDeleteDialogOpen(true);
        }
    };

    const confirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsDeleting(true);
            await dataService.deleteBerichtstermin(itemToDelete.itemId);
            await fetchBerichte();
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error deleting report:', err);
            setError('Error deleting report');
        } finally {
            setIsDeleting(false);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, item: any) => {
        setDraggedItem(item);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
        }
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverItem(null);
        setDropPosition(null);
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, item: any) => {
        e.preventDefault();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
        }

        if (!draggedItem || draggedItem.itemId === item.itemId) {
            return;
        }

        setDragOverItem(item);

        // Calculate position (before/after) based on Y position in row
        const rect = e.currentTarget.getBoundingClientRect();
        const midpoint = rect.height / 2;
        const offsetY = e.clientY - rect.top;

        setDropPosition(offsetY < midpoint ? 'before' : 'after');
    };

    const handleDragLeave = () => {
        setDragOverItem(null);
        setDropPosition(null);
    };

    const handleDrop = async (e: React.DragEvent<HTMLTableRowElement>, targetItem: any) => {
        e.preventDefault();

        if (!draggedItem || draggedItem.itemId === targetItem.itemId || !dataService) {
            handleDragEnd();
            return;
        }

        try {
            // Helper function to convert Date to ISO date string
            const dateToISOString = (date: string): string => {
                if (!date) {
                    return '0001-01-01';
                }
                // If it's already an ISO string, extract just the date part
                if (typeof date === 'string' && date.includes('T')) {
                    return date.split('T')[0];
                }
                return date;
            };

            // Determine insertAfterId based on drop position
            let insertAfterId = targetItem.itemId;

            if (dropPosition === 'before') {
                // Find the item before targetItem
                const targetIndex = berichte.findIndex(m => m.itemId === targetItem.itemId);
                if (targetIndex > 0) {
                    insertAfterId = berichte[targetIndex - 1].itemId;
                } else {
                    // Inserting at the beginning - use -2 as special value
                    insertAfterId = -2;
                }
            }

            const submitData = {
                itemId: draggedItem.itemId,
                projektID: draggedItem.projektID,
                bezeichnung: draggedItem.bezeichnung,
                datumVon: dateToISOString(draggedItem.datumVon),
                datumBis: dateToISOString(draggedItem.datumBis),
                datumFaelligkeit: dateToISOString(draggedItem.datumFaelligkeit),
                bemerkungen: draggedItem.bemerkungen,
                isZwischenevaluierung: draggedItem.isZwischenevaluierung,
                isInhaltlicherBericht: draggedItem.isInhaltlicherBericht,
                isFinanziellerBericht: draggedItem.isFinanziellerBericht,
                isEingereicht: draggedItem.isEingereicht,
                isAngenommen: draggedItem.isAngenommen,
            } as any;

            await dataService.updateBerichtstermin(submitData, insertAfterId);
            await fetchBerichte();
        } catch (err) {
            console.error('Error reordering report:', err);
            setError('Error reordering report');
        } finally {
            handleDragEnd();
        }
    };

    // Check if form is valid
    const isFormValid = useMemo(() => {
        if (!formData.bezeichnung || formData.bezeichnung.trim() === '') {
            return false; // Designation is required
        }
        if (!formData.datumVon || !formData.datumBis || !formData.datumFaelligkeit) {
            return false; // Dates are required
        }

        // Add mode specific validation
        if (!editingId) {
            if (formData.orderBy === 0 || formData.orderBy === null || formData.orderBy === undefined) {
                return false; // Insert position must be selected
            }
        }

        return true;
    }, [formData, editingId]);

    const handleSave = async () => {
        if (!dataService || !formData.bezeichnung) {
            setError('Please fill in required fields');
            return;
        }

        try {
            setIsLoading(true);

            // Helper function to convert Date to ISO date string
            const dateToISOString = (date: Date | null): string => {
                if (!date) {
                    return '0001-01-01';
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const submitData: BerichtsTerminItem = {
                itemId: formData.itemId,
                projektID: formData.projektID || project?.projektID || 0,
                bezeichnung: formData.bezeichnung,
                datumVon: dateToISOString(formData.datumVon) as any,
                datumBis: dateToISOString(formData.datumBis) as any,
                datumFaelligkeit: dateToISOString(formData.datumFaelligkeit) as any,
                bemerkungen: formData.bemerkungen || '',
                isZwischenevaluierung: formData.isZwischenevaluierung || false,
                isInhaltlicherBericht: formData.isInhaltlicherBericht || false,
                isFinanziellerBericht: formData.isFinanziellerBericht || false,
                isEingereicht: formData.isEingereicht || false,
                isAngenommen: formData.isAngenommen || false,
                orderBy: formData.orderBy || 0,
            } as any;

            if (editingId) {
                await dataService.updateBerichtstermin(submitData);
            } else {
                await dataService.addBerichtstermin(submitData, formData.orderBy || 0);
            }

            setIsDrawerOpen(false);
            await fetchBerichte();
        } catch (err) {
            console.error('Error saving report:', err);
            setError('Error saving report');
        } finally {
            setIsLoading(false);
        }
    };

    if (!project) return null;

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <Button
                    icon={<Add24Regular />}
                    onClick={handleAddNew}
                    appearance="primary"
                >
                    Add Report
                </Button>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {isLoading && !berichte.length ? (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading reports..." />
                </div>
            ) : berichte.length === 0 ? (
                <div className={styles.emptyState}>No reports found</div>
            ) : (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell style={{ width: '40px' }}></TableHeaderCell>
                                <TableHeaderCell className={styles.colActions}>Actions</TableHeaderCell>
                                <TableHeaderCell className={styles.colNumber}>Report Number</TableHeaderCell>
                                <TableHeaderCell>Designation</TableHeaderCell>
                                <TableHeaderCell className={styles.colDate}>Report From</TableHeaderCell>
                                <TableHeaderCell className={styles.colDate}>Report To</TableHeaderCell>
                                <TableHeaderCell className={styles.colDate}>Due Date</TableHeaderCell>
                                <TableHeaderCell>Interim Evaluation</TableHeaderCell>
                                <TableHeaderCell>Submitted</TableHeaderCell>
                                <TableHeaderCell>Accepted</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {berichte.map(item => (
                                <TableRow
                                    key={item.itemId}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, item)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => handleDragOver(e, item)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, item)}
                                    style={{
                                        opacity: draggedItem?.itemId === item.itemId ? 0.5 : 1,
                                        backgroundColor: dragOverItem?.itemId === item.itemId ? '#f0f0f0' : 'transparent',
                                        borderTop: dragOverItem?.itemId === item.itemId && dropPosition === 'before' ? '3px solid #107c10' : 'none',
                                        borderBottom: dragOverItem?.itemId === item.itemId && dropPosition === 'after' ? '3px solid #ff8c00' : 'none',
                                        position: 'relative',
                                    }}
                                >
                                    <TableCell>
                                        <span className={styles.dragHandle} title="Drag to reorder">⋮⋮</span>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            icon={<Eye24Regular />}
                                            size="small"
                                            onClick={() => handleView(item)}
                                            title="View"
                                        />
                                        <Button
                                            icon={<Edit24Regular />}
                                            size="small"
                                            onClick={() => handleEdit(item)}
                                            title="Edit"
                                        />
                                        <Button
                                            icon={<Delete24Regular />}
                                            size="small"
                                            onClick={() => handleDelete(item.itemId)}
                                            title="Delete"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.prefixTitle}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bezeichnung}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>
                                            {item.datumVon ? new Date(item.datumVon).toLocaleDateString() : '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>
                                            {item.datumBis ? new Date(item.datumBis).toLocaleDateString() : '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>
                                            {item.datumFaelligkeit ? new Date(item.datumFaelligkeit).toLocaleDateString() : '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.isZwischenevaluierung ? 'Yes' : 'No'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.isEingereicht ? 'Yes' : 'No'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.isAngenommen ? 'Yes' : 'No'}</TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Drawer
                open={isDrawerOpen}
                onOpenChange={(_, { open }) => setIsDrawerOpen(open)}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>
                        {isViewOnly ? 'View Report' : (editingId ? 'Edit Report' : 'Add Report')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        {/* Report Properties Section */}
                        <div>
                            <div className={styles.sectionHeader}>Report Properties</div>
                            <div className={styles.sectionDescription}>
                                Report number (automatically assigned), designation, dates and other information
                            </div>

                            {/* Report Number (Read-only) */}
                            {(editingId || isViewOnly) && (
                                <div className={styles.formGroup}>
                                    <Label>Report Number</Label>
                                    <div className={styles.readOnlyField}>
                                        {editingId ? (berichte.find(b => b.itemId === editingId)?.prefixTitle || '-') : '-'}
                                    </div>
                                </div>
                            )}

                            {/* Designation */}
                            <div className={styles.formGroup}>
                                <Label required>Designation</Label>
                                <Input
                                    value={formData.bezeichnung}
                                    disabled={isViewOnly}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, bezeichnung: data.value });
                                    }}
                                    placeholder="Enter designation"
                                />
                            </div>

                            {/* Report From Date */}
                            <div className={styles.formGroup}>
                                <Label required>Report From</Label>
                                <DatePicker
                                    value={formData.datumVon}
                                    disabled={isViewOnly}
                                    onSelectDate={(date) => {
                                        setFormData({ ...formData, datumVon: date || null });
                                    }}
                                    placeholder="Select start date"
                                />
                            </div>

                            {/* Report To Date */}
                            <div className={styles.formGroup}>
                                <Label required>Report To</Label>
                                <DatePicker
                                    value={formData.datumBis}
                                    disabled={isViewOnly}
                                    onSelectDate={(date) => {
                                        setFormData({ ...formData, datumBis: date || null });
                                    }}
                                    placeholder="Select end date"
                                />
                            </div>

                            {/* Due Date */}
                            <div className={styles.formGroup}>
                                <Label required>Due Date</Label>
                                <DatePicker
                                    value={formData.datumFaelligkeit}
                                    disabled={isViewOnly}
                                    onSelectDate={(date) => {
                                        setFormData({ ...formData, datumFaelligkeit: date || null });
                                    }}
                                    placeholder="Select due date"
                                />
                            </div>

                            {/* Notes */}
                            <div className={styles.formGroup}>
                                <Label>Notes</Label>
                                <Textarea
                                    value={formData.bemerkungen || ''}
                                    disabled={isViewOnly}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, bemerkungen: data.value });
                                    }}
                                    placeholder="Enter notes"
                                />
                            </div>

                            {/* Insert After - only show when not viewing */}
                            {!isViewOnly && !editingId && (
                                <div className={styles.formGroup}>
                                    <Label required>Insert After</Label>
                                    <Select
                                        value={formData.orderBy?.toString() || ''}
                                        onChange={(_e, data) => {
                                            setFormData({
                                                ...formData,
                                                orderBy: data.value ? parseInt(data.value, 10) : 0,
                                            });
                                        }}
                                    >
                                        {insertAfterOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </div>

                        {/* Report Status Section */}
                        <div>
                            <div className={styles.sectionHeader}>Report Status</div>
                            <div className={styles.sectionDescription}>
                                Status information for the report submission and acceptance
                            </div>

                            {/* Zwischenevaluierung */}
                            <div className={styles.formGroup}>
                                <Label required>Interim Evaluation</Label>
                                <RadioGroup
                                    layout="horizontal"
                                    value={formData.isZwischenevaluierung === true ? 'yes' : 'no'}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, isZwischenevaluierung: data.value === 'yes' });
                                    }}
                                    disabled={isViewOnly}
                                >
                                    <Radio value="yes" label="Yes" />
                                    <Radio value="no" label="No" />
                                </RadioGroup>
                            </div>

                            {/* Inhaltlicher Bericht */}
                            <div className={styles.formGroup}>
                                <Label required>Content Report</Label>
                                <RadioGroup
                                    layout="horizontal"
                                    value={formData.isInhaltlicherBericht === true ? 'yes' : 'no'}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, isInhaltlicherBericht: data.value === 'yes' });
                                    }}
                                    disabled={isViewOnly}
                                >
                                    <Radio value="yes" label="Yes" />
                                    <Radio value="no" label="No" />
                                </RadioGroup>
                            </div>

                            {/* Finanzieller Bericht */}
                            <div className={styles.formGroup}>
                                <Label required>Financial Report</Label>
                                <RadioGroup
                                    layout="horizontal"
                                    value={formData.isFinanziellerBericht === true ? 'yes' : 'no'}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, isFinanziellerBericht: data.value === 'yes' });
                                    }}
                                    disabled={isViewOnly}
                                >
                                    <Radio value="yes" label="Yes" />
                                    <Radio value="no" label="No" />
                                </RadioGroup>
                            </div>

                            {/* Eingereicht */}
                            <div className={styles.formGroup}>
                                <Label required>Submitted</Label>
                                <RadioGroup
                                    layout="horizontal"
                                    value={formData.isEingereicht === true ? 'yes' : 'no'}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, isEingereicht: data.value === 'yes' });
                                    }}
                                    disabled={isViewOnly}
                                >
                                    <Radio value="yes" label="Yes" />
                                    <Radio value="no" label="No" />
                                </RadioGroup>
                            </div>

                            {/* Angenommen */}
                            <div className={styles.formGroup}>
                                <Label required>Accepted</Label>
                                <RadioGroup
                                    layout="horizontal"
                                    value={formData.isAngenommen === true ? 'yes' : 'no'}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, isAngenommen: data.value === 'yes' });
                                    }}
                                    disabled={isViewOnly}
                                >
                                    <Radio value="yes" label="Yes" />
                                    <Radio value="no" label="No" />
                                </RadioGroup>
                            </div>
                        </div>
                    </div>
                </DrawerBody>
                <DrawerFooter>
                    <Button
                        appearance="secondary"
                        onClick={() => setIsDrawerOpen(false)}
                    >
                        {isViewOnly ? 'Close' : 'Cancel'}
                    </Button>
                    {!isViewOnly && (
                        <Button
                            appearance="primary"
                            onClick={handleSave}
                            disabled={!isFormValid}
                        >
                            Save
                        </Button>
                    )}
                </DrawerFooter>
            </Drawer>
            <Dialog open={deleteDialogOpen}>
                {/* Delete Confirmation Dialog */}
                <DialogSurface style={{ minWidth: '480px', maxWidth: '600px' }}>
                    <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                        <DialogTitle>Delete Report</DialogTitle>
                        <DialogBody style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ width: '100%' }}>
                                <p style={{ color: tokens.colorStatusDangerForeground1, fontWeight: 600, marginBottom: '12px' }}>
                                    ⚠️ This action cannot be undone
                                </p>
                                <p>
                                    Are you sure you want to delete this report?
                                </p>
                                {itemToDelete && (
                                    <div style={{
                                        backgroundColor: tokens.colorNeutralBackground2,
                                        padding: '12px',
                                        borderRadius: tokens.borderRadiusMedium,
                                        marginTop: '12px',
                                    }}>
                                        <div><strong>Report Number:</strong> {itemToDelete.prefixTitle}</div>
                                        <div><strong>Designation:</strong> {itemToDelete.bezeichnung}</div>
                                        <div><strong>From:</strong> {itemToDelete.datumVon ? new Date(itemToDelete.datumVon).toLocaleDateString() : '-'}</div>
                                        <div><strong>To:</strong> {itemToDelete.datumBis ? new Date(itemToDelete.datumBis).toLocaleDateString() : '-'}</div>
                                    </div>
                                )}
                            </div>
                        </DialogBody>
                        <div style={{
                            display: 'flex',
                            gap: tokens.spacingHorizontalM,
                            justifyContent: 'flex-end',
                            padding: tokens.spacingHorizontalL,
                            borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                        }}>
                            <Button
                                appearance="secondary"
                                onClick={cancelDelete}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    </DialogContent>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default ProjektmanagementBerichte;
